import React, { createContext, useState, useContext, ReactNode, useCallback } from "react";
import { useDispatch } from "react-redux";
import DealerContentSettings, {
  DealerContentSetting,
  CTAButton,
} from "../apiService/dealerContentSettings/dealerContentSettingsApiService";
import { headerActions } from "../pages/Header/slice";

function touchesHarmonizedDealerFields(
  settings: Partial<DealerContentSetting>,
): boolean {
  if (
    settings.logo_bar_url !== undefined ||
    settings.standard_disclaimer_text !== undefined ||
    settings.footer_image_url !== undefined ||
    settings.footer_url !== undefined ||
    settings.cta_buttons !== undefined
  ) {
    return true;
  }

  if (settings.primary_contact !== undefined) {
    return settings.primary_contact.website !== undefined;
  }

  return false;
}

// ----------- Type Definitions -----------
interface DealerContentSettingsContextType {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;

  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;

  success: boolean;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;

  dealerContentSettings: DealerContentSetting | null;
  setDealerContentSettings: React.Dispatch<React.SetStateAction<DealerContentSetting | null>>;

  // API Methods
  getDealerContentSettings: (dealerId: string) => Promise<void>;
  createDealerContentSettings: (settings: DealerContentSetting) => Promise<void>;
  updateDealerContentSettings: (dealerId: string, settings: Partial<DealerContentSetting>) => Promise<void>;
  deleteDealerContentSettings: (dealerId: string) => Promise<void>;
  
  // CTA Button Methods
  addCtaButton: (dealerId: string, button: CTAButton) => Promise<void>;
  updateCtaButton: (dealerId: string, button: CTAButton) => Promise<void>;
  deleteCtaButton: (dealerId: string, buttonId: string) => Promise<void>;
  
  // Helper Methods
  getDefaultDealerContentSettings: (dealerId: string, dealerName?: string) => DealerContentSetting;
}

// ----------- Context Create -----------
const DealerContentSettingsContext = createContext<DealerContentSettingsContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const DealerContentSettingsProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [dealerContentSettings, setDealerContentSettings] = useState<DealerContentSetting | null>(null);

  const refreshDealerListIfNeeded = useCallback(
    (settings: Partial<DealerContentSetting>) => {
      if (touchesHarmonizedDealerFields(settings)) {
        dispatch(headerActions.requestDealers({ forceRefresh: true }));
      }
    },
    [dispatch],
  );

  // ----------- Default Settings Generator -----------
  const getDefaultDealerContentSettings = (dealerId: string, dealerName?: string): DealerContentSetting => {
    // Create dealer domain from dealer name
    const dealerDomain = dealerName 
      ? `www.${dealerName.toLowerCase().replace(/[^a-z0-9]/g, '')}.com`
      : 'www.dealer.com';

    return {
      dealer: dealerId,
      primary_contact: {},
      invoice_contact: {},
      secondary_contact: {},
      days_of_service: {},
      make: [],
      manufacturer: undefined,
      marketing_data: {},
      logo_bar_url: {
        title: 'Logo Bar URL',
        value: '',
        type: 'url',
      },
      address: {
        title: 'Address',
        value: '',
        type: 'text',
      },
      phone: {
        title: 'Phone',
        value: '',
        type: 'phone',
      },
      standard_disclaimer_text: {
        title: 'Standard Disclaimer Text',
        value: '',
        type: 'textarea',
      },
      footer_url: {
        title: 'Footer URL',
        value: '',
        type: 'url',
      },
      footer_image_url: {
        title: 'Footer Image URL',
        value: '',
        type: 'url',
      },
      cta_buttons: [
        {
          id: '1',
          name: 'Trade-in',
          url: `https://${dealerDomain}/trade-in`,
          type: 'FORM',
          tags: ['trade-in'],
        },
        {
          id: '2',
          name: 'Finance',
          url: `https://${dealerDomain}/finance`,
          type: 'FORM',
          tags: ['finance'],
        },
        {
          id: '3',
          name: 'Inventory',
          url: `https://${dealerDomain}/inventory`,
          type: 'SRP',
          tags: ['inventory'],
        },
      ],
      content_font: {
        title: 'Content Font',
        value: 'Roboto',
        type: 'font',
      },
      content_font_color: {
        title: 'Content Font Color',
        value: '#666666',
        type: 'color',
      },
      content_font_size: {
        title: 'Content Font Size',
        value: '14',
        type: 'font-size',
      },
      primary_headline_font_color: {
        title: 'Primary Headline Font Color',
        value: '#000000',
        type: 'color',
      },
      primary_headline_size: {
        title: 'Primary Headline Size',
        value: '22',
        type: 'font-size',
      },
      headline_highlight_font_color: {
        title: 'Headline Highlight Font Color',
        value: '#c3002f',
        type: 'color',
      },
      headline_highlight_font_size: {
        title: 'Headline Highlight Font Size',
        value: '18',
        type: 'font-size',
      },
      disclaimer_font: {
        title: 'Disclaimer Font',
        value: 'Roboto',
        type: 'font',
      },
      disclaimer_font_color: {
        title: 'Disclaimer Font Color',
        value: '#808080',
        type: 'color',
      },
      disclaimer_font_size: {
        title: 'Disclaimer Font Size',
        value: '14',
        type: 'font-size',
      },
      primary_cta_button_background_color: {
        title: 'Primary CTA Button Background Color',
        value: '#c3002f',
        type: 'color',
      },
      primary_cta_button_border_size: {
        title: 'Primary CTA Button Border Size',
        value: '1',
        type: 'border-size',
      },
      primary_cta_button_border_color: {
        title: 'Primary CTA Button Border Color',
        value: '#c3002f',
        type: 'color',
      },
      primary_cta_button_font: {
        title: 'Primary CTA Button Font',
        value: 'Roboto',
        type: 'font',
      },
      primary_cta_button_font_color: {
        title: 'Primary CTA Button Font Color',
        value: '#FFFFFF',
        type: 'color',
      },
      primary_cta_button_font_size: {
        title: 'Primary CTA Button Font Size',
        value: '16',
        type: 'font-size',
      },
      secondary_cta_button_background_color: {
        title: 'Secondary CTA Button Background Color',
        value: '#FFFFFF',
        type: 'color',
      },
      secondary_cta_button_border_size: {
        title: 'Secondary CTA Button Border Size',
        value: '2',
        type: 'border-size',
      },
      secondary_cta_button_border_color: {
        title: 'Secondary CTA Button Border Color',
        value: '#c3002f',
        type: 'color',
      },
      secondary_cta_button_font: {
        title: 'Secondary CTA Button Font',
        value: 'Roboto',
        type: 'font',
      },
      secondary_cta_button_font_color: {
        title: 'Secondary CTA Button Font Color',
        value: '#c3002f',
        type: 'color',
      },
      secondary_cta_button_font_size: {
        title: 'Secondary CTA Button Font Size',
        value: '16',
        type: 'font-size',
      },
    };
  };

  // ----------- API Methods -----------
  const getDealerContentSettings = async (dealerId: string) => {
    if (!dealerId) return;

    // Clear existing settings immediately when a new dealer is selected
    setDealerContentSettings(null);
    setLoading(true);
    setError("");
    try {
      const response = await DealerContentSettings.getDealerContentSettings(dealerId);
      
      const settings = Array.isArray(response.body?.data)
        ? response.body.data[0]
        : response.body;
      
      // Add _id to settings if it exists in response
      if (settings && response.body?.data?.[0]?._id) {
        settings._id = response.body.data[0]._id;
      }
      
      // If no settings found, use default settings
      if (!settings || Object.keys(settings).length === 0) {
      } else {
        setDealerContentSettings(settings);
      }
      
      setSuccess(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load dealer content settings";
      setError(errorMessage);
      // console.log('getDealerContentSettings - error, using default settings');
      // Set default settings if API fails
      // setDealerContentSettings(getDefaultDealerContentSettings(dealerId));
    } finally {
      setLoading(false);
    }
  };

  const createDealerContentSettings = async (settings: DealerContentSetting) => {
    setLoading(true);
    setError("");
    try {
      const dealerId = settings.dealer;
      await DealerContentSettings.createDealerContentSettings(settings);

      // Reload from server so dealer-info seeding and persisted defaults are shown
      if (dealerId) {
        setDealerContentSettings(null);
        await getDealerContentSettings(dealerId);
      }

      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create dealer content settings");
    } finally {
      setLoading(false);
    }
  };

  const updateDealerContentSettings = async (dealerId: string, settings: Partial<DealerContentSetting>) => {
    setLoading(true);
    setError("");
    try {
      // Always use _id for updates - throw error if _id is not available
      if (!dealerContentSettings?._id) {
        throw new Error("Dealer content settings _id is required for update. Please ensure settings are created first.");
      }
      
      const updateId = dealerContentSettings._id;
      await DealerContentSettings.updateDealerContentSettings(updateId, settings);
      
      // Refresh data from server after successful update to ensure we have latest state
      // Clear previous state first to avoid showing stale data
      setDealerContentSettings(null);
      await getDealerContentSettings(dealerId);
      refreshDealerListIfNeeded(settings);
      
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update dealer content settings");
    } finally {
      setLoading(false);
    }
  };

  const deleteDealerContentSettings = async (dealerId: string) => {
    setLoading(true);
    setError("");
    try {
      await DealerContentSettings.deleteDealerContentSettings(dealerId);
      setDealerContentSettings(null);
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete dealer content settings");
    } finally {
      setLoading(false);
    }
  };

  // ----------- CTA Button Methods -----------
  const addCtaButton = async (dealerId: string, button: CTAButton) => {
    if (!dealerContentSettings) return;

    setLoading(true);
    setError("");
    try {
      // Always use _id for updates - throw error if _id is not available
      if (!dealerContentSettings._id) {
        throw new Error("Dealer content settings _id is required for update. Please ensure settings are created first.");
      }
      
      const updatedCtaButtons = [...(dealerContentSettings.cta_buttons || []), button];
      
      // Only send the cta_buttons field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        cta_buttons: updatedCtaButtons,
      };

      await DealerContentSettings.updateDealerContentSettings(dealerContentSettings._id, updatePayload);

      // Refresh data from server after successful update
      setDealerContentSettings(null);
      await getDealerContentSettings(dealerId);
      refreshDealerListIfNeeded(updatePayload);
      
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add CTA button");
    } finally {
      setLoading(false);
    }
  };

  const updateCtaButton = async (dealerId: string, button: CTAButton) => {
    if (!dealerContentSettings) return;

    setLoading(true);
    setError("");
    try {
      // Always use _id for updates - throw error if _id is not available
      if (!dealerContentSettings._id) {
        throw new Error("Dealer content settings _id is required for update. Please ensure settings are created first.");
      }
      
      const updatedCtaButtons = (dealerContentSettings.cta_buttons || []).map(btn =>
        btn.id === button.id ? button : btn
      );
      
      // Only send the cta_buttons field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        cta_buttons: updatedCtaButtons,
      };

      await DealerContentSettings.updateDealerContentSettings(dealerContentSettings._id, updatePayload);

      // Refresh data from server after successful update
      setDealerContentSettings(null);
      await getDealerContentSettings(dealerId);
      refreshDealerListIfNeeded(updatePayload);
      
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update CTA button");
    } finally {
      setLoading(false);
    }
  };

  const deleteCtaButton = async (dealerId: string, buttonId: string) => {
    if (!dealerContentSettings) return;

    setLoading(true);
    setError("");
    try {
      // Always use _id for updates - throw error if _id is not available
      if (!dealerContentSettings._id) {
        throw new Error("Dealer content settings _id is required for update. Please ensure settings are created first.");
      }
      
      const updatedCtaButtons = (dealerContentSettings.cta_buttons || []).filter(btn => btn.id !== buttonId);
      
      // Only send the cta_buttons field in the payload
      const updatePayload: Partial<DealerContentSetting> = {
        cta_buttons: updatedCtaButtons,
      };

      await DealerContentSettings.updateDealerContentSettings(dealerContentSettings._id, updatePayload);

      // Refresh data from server after successful update
      setDealerContentSettings(null);
      await getDealerContentSettings(dealerId);
      refreshDealerListIfNeeded(updatePayload);
      
      setSuccess(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete CTA button");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DealerContentSettingsContext.Provider
      value={{
        loading,
        setLoading,
        error,
        setError,
        success,
        setSuccess,
        dealerContentSettings,
        setDealerContentSettings,
        getDealerContentSettings,
        createDealerContentSettings,
        updateDealerContentSettings,
        deleteDealerContentSettings,
        addCtaButton,
        updateCtaButton,
        deleteCtaButton,
        getDefaultDealerContentSettings,
      }}
    >
      {children}
    </DealerContentSettingsContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useDealerContentSettings = (): DealerContentSettingsContextType => {
  const context = useContext(DealerContentSettingsContext);
  if (context === undefined) {
    throw new Error("useDealerContentSettings must be used within a DealerContentSettingsProvider");
  }
  return context;
}; 